import { Component, HostListener, OnInit } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ProdutosService } from '../../../services/produtos/produto.service';
import { ApiService } from '../../../services/api.service';
import { UnidadeMedidaDeItemService } from '../../../services/configuracoes/unidade-de-medida.service';
import { GrupoService } from '../../../services/grupo/grupo.service';
import { MarcaService } from '../../../services/marca/marca.service';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { ProdutosDTO } from '../../../models/produto.models';
import { TipoDeImposto } from '../../../enums/tipoDeImposto.enum';
import { TipoDeDesconto } from '../../../enums/TipoDeDesconto.enum';
import { TipoItem } from '../../../enums/TipoItem.enum';
import { SubGrupoService } from '../../../services/grupo/sub-grupo.service';
import { EmpresaConfigService } from '../../../services/configuracoes/empresa.service';
import { ImpressoraService } from '../../../services/impressora/impressora.service';
import { MoedaDTO } from '../../../models/configuracoes/empresa.model';
import { Subscription, forkJoin } from 'rxjs';
import { AjusteEstoqueService } from '../../../services/estoque/ajuste-estoque.service';
import { AjusteDeEstoqueDTO, TipoOperacaoDeEstoque, TipoContaEstoque } from '../../../models/estoque/ajuste-estoque.model';
import { ProdutoVarianteModificadorService } from '../../../services/produto-variante-modificador/produto-variante-modificador.service';
import {
  VarianteDePrecoDeItemDTO,
  GrupoDeModificadorDePrecoDeItemDTO,
  ModificadorDePrecoDeItemDTO,
} from '../../../models/produto-variante-modificador/produto-variante-modificador.model';
import { toLocalISOString } from '../../../shared/utils/date-utils';
import { validarPromocaoNovidade } from '../../../shared/utils/promocao-novidade.util';
import { extrairErros400, sanitizarMensagemErro } from '../../../shared/utils/error-utils';
import { ImagemUtils } from '../../../shared/utils/imagem-utils';

@Component({
  selector: 'app-produtos',
  templateUrl: './produtos.component.html',
  styleUrls: ['./produtos.component.scss'],
  providers: [MessageService],
})
export class ProdutosComponent implements OnInit {
  habilitarMiniShoppingEmpresaConfig: boolean = false;
  imprimirItensDeAcordoImpressoraEmpresaConfig: boolean = false;
  form: ProdutosDTO = this.createEmptyForm();
  unidadesMedida: { label: string; value: number }[] = [];
  grupos: { label: string; value: number }[] = [];
  subGrupos: { label: string; value: number }[] = [];
  marcas: { label: string; value: number }[] = [];
  fornecedores: { label: string; value: number }[] = [];
  impressoras: { label: string; value: number }[] = [];
  habilitarMiniShopping: { label: string; value: boolean }[] = [];
  permiteImprimir: { label: string; value: boolean }[] = [];
 
  tiposDeDesconto = Object.values(TipoDeDesconto).map((value) => ({
    label: value,
    value,
  }));

  // ---- Upload / preview
  imageBase64: string | null = null;       // Base64 SEM prefixo (o que vai para o back)
  imagePreviewUrl: string | null = null;   // DataURL para pré-visualização
  fileName: string | null = null;

  // Variantes de preco (locais, serao salvas apos criar o produto)
  variantes: VarianteDePrecoDeItemDTO[] = [];

  // Grupos de modificadores (locais, serao salvos apos criar o produto)
  gruposModificadores: GrupoDeModificadorDePrecoDeItemDTO[] = [];

  tipoDeItensOptions: { value: string; label: string }[] = [];
  tipoDeImpostoOptions: { value: string; label: string }[] = [];

  moedaPadrao: MoedaDTO | undefined;

  // Mapa de id da unidade de medida → casas decimais (para campos de estoque)
  private unidadeMedidaCasasDecimaisMap: Map<number, number> = new Map();
  casasDecimaisEstoque: number = 0;

  // ---- Promocao e Novidade (estado dos p-calendar fora do form: ProdutosDTO)
  promocao = false;
  promocaoAteEmData: Date | null = null;
  novidade = false;
  novidadeAteEmData: Date | null = null;
  /** Data minima dos calendarios — impede selecao no passado (reforco de RN02/RN04). */
  dataMinimaPromocaoNovidade: Date = new Date();

  constructor(
    private produtosService: ProdutosService,
    private apiService: ApiService,
    private unidadeMedidaService: UnidadeMedidaDeItemService,
    private grupoService: GrupoService,
    private subGrupoService: SubGrupoService,
    private marcaService: MarcaService,
    private pessoaService: PessoaService,
    private empresaConfigService: EmpresaConfigService,
    private impressoraService: ImpressoraService,
    private messageService: MessageService,
    private router: Router,
    private translate: TranslateService,
    private produtoVarianteModificadorService: ProdutoVarianteModificadorService,
    private ajusteEstoqueService: AjusteEstoqueService,
  ) { }

  private langChangeSub!: Subscription;

  ngOnInit(): void {
    this.loadTranslate();
    this.loadUnidadesMedida();
    this.loadGrupos();
    this.loadSubGrupos();
    this.loadMarcas();
    this.loadFornecedores();
    this.loadEmpresaConfig();
    this.loadImpressoras();
    this.carregarMoedaPadrao();

    this.buildTipoDeItensOptions();
    this.buildTipoDeImpostoOptions();
    this.preSelectTipoItem();

    // Atualiza labels quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.loadTranslate();
      this.buildTipoDeItensOptions();
      this.buildTipoDeImpostoOptions();
      this.preSelectTipoItem();
    });
  }

  get locale(): string {
    return this.moedaPadrao?.pais?.local?.replaceAll('_', '-') || 'es-PY';
  }

  get currencyCode(): string {
    return this.moedaPadrao?.codigoOperacao || 'PYG';
  }

  get currencySymbol(): string {
    const codigo = this.currencyCode.toUpperCase();
    switch (codigo) {
      case 'BRL': return 'R$';
      case 'USD': return '$';
      case 'PYG': return 'Gs';
      default: return 'Gs';
    }
  }

  get casasDecimaisDaMoeda(): number {
    return this.moedaPadrao?.casasDecimais ?? 0;
  }

  /** `true` quando o tipo de item selecionado e SERVICO.
   *  Mantido por ser utilizado nas validacoes de campos de estoque (custo,
   *  saldo, estoque minimo) — servicos nao exigem esses campos. */
  get ehServico(): boolean {
    return this.form?.tipoItem?.value === TipoItem.SERVICO;
  }

  /** `true` quando o preco de venda e invalido.
   *  Regra unica para qualquer tipo de item: invalido apenas se nulo/undefined.
   *  Zero e valores positivos sao validos para qualquer tipo. */
  get precoDeVendaInvalido(): boolean {
    return this.form?.precoDeVenda == null;
  }

  formatarCampoPreco(event: any, campo: 'precoDeVenda' | 'custo'): void {
    const casasDecimais = this.moedaPadrao?.casasDecimais ?? 0;
    const valor = this.extrairValorNumerico(event.target.value, casasDecimais);
    (this.form as any)[campo] = valor;
    event.target.value = valor !== null ? String(valor) : '';
  }

  formatarCampoPrecoVariante(event: any, variante: VarianteDePrecoDeItemDTO): void {
    const casasDecimais = this.moedaPadrao?.casasDecimais ?? 0;
    const valor = this.extrairValorNumerico(event.target.value, casasDecimais);
    variante.precoDeVenda = valor ?? 0;
    event.target.value = valor !== null ? String(valor) : '';
  }

  formatarCampoPrecoModificador(event: any, mod: ModificadorDePrecoDeItemDTO): void {
    const casasDecimais = this.moedaPadrao?.casasDecimais ?? 0;
    const valor = this.extrairValorNumerico(event.target.value, casasDecimais);
    mod.precoDeVenda = valor ?? 0;
    event.target.value = valor !== null ? String(valor) : '';
  }

  private extrairValorNumerico(valor: string, casasDecimais: number): number | null {
    if (casasDecimais === 0) {
      const limpo = valor.replace(/[^\d-]/g, '');
      if (limpo === '' || limpo === '-') return null;
      return parseInt(limpo, 10);
    } else {
      const limpo = valor.replace(/[^\d.-]/g, '');
      if (limpo === '' || limpo === '-') return null;
      return parseFloat(limpo);
    }
  }

  carregarMoedaPadrao(): void {
    const idEmpresa = sessionStorage.getItem('id') || '';
    if (!idEmpresa) return;

    this.empresaConfigService.get(`/configuracaoEmpresa/${idEmpresa}`).subscribe({
      next: (res) => {
        this.moedaPadrao = res?.moeda;
      },
      error: (err) => {
        console.error('Erro ao buscar configuração da empresa:', err);
      },
    });
  }

  loadTranslate() {
    this.translate.get(['produtos.labels.sim', 'produtos.labels.nao']).subscribe(translations => {
      this.habilitarMiniShopping = [
        { label: translations['produtos.labels.sim'], value: true },
        { label: translations['produtos.labels.nao'], value: false }
      ];
      this.permiteImprimir = [
        { label: translations['produtos.labels.sim'], value: true },
        { label: translations['produtos.labels.nao'], value: false }
      ];
    });
  }

  loadUnidadesMedida(): void {
    this.unidadeMedidaService.get('/unidaDeMedidaDeItemEmpresa', { page: 0, size: 1000, sort: 'id,asc' }).subscribe({
      next: (response: any) => {
        this.unidadeMedidaCasasDecimaisMap.clear();
        this.unidadesMedida = (response.content || []).map((item: any) => {
          const id = item.unidadeMedidaDeItemEmpresa?.id ?? item.id ?? 0;
          const casas = item.unidadeMedidaDeItemEmpresa?.casasDecimais ?? item.casasDecimais ?? 0;
          this.unidadeMedidaCasasDecimaisMap.set(id, casas);
          return {
            label: item.unidadeMedidaDeItemEmpresa?.nome ?? item.nome,
            value: id,
          };
        });
        this.preencherUnidadeMedidaPadrao();
      },
      error: () => {
        // Unidades de medida são opcionais — falha silenciosa
        this.unidadesMedida = [];
      },
    });
  }

  /** Pre-preenche o campo de unidade de medida com a unidade padrao da empresa */
  private preencherUnidadeMedidaPadrao(): void {
    // Nao sobrescreve se o usuario ja selecionou uma unidade
    if (this.form.unidadeMedidaDeItem_id && (this.form.unidadeMedidaDeItem_id as any) !== 0) {
      return;
    }

    this.apiService.read<any>('/configuracaoGeral').subscribe({
      next: (response: any) => {
        const idPadrao = response?.content?.[0]?.unidadeMedidaPadraoId;
        if (!idPadrao) return;

        const opcao = this.unidadesMedida.find((u) => u.value === idPadrao);
        if (opcao) {
          this.form.unidadeMedidaDeItem_id = idPadrao;
          this.atualizarCasasDecimaisEstoque(idPadrao);
        }
      },
      error: () => {
        // Unidade padrao e opcional — falha silenciosa
      },
    });
  }

  /** Handler chamado quando a unidade de medida muda no dropdown */
  onUnidadeMedidaChange(): void {
    const id = this.form.unidadeMedidaDeItem_id as number;
    this.atualizarCasasDecimaisEstoque(id ?? 0);
  }

  /**
   * Handler chamado ao focar o campo de preco de venda.
   *
   * Workaround para um problema do p-inputNumber em moedas sem casas decimais
   * (ex.: PYG): se o campo ja contem 0 e o usuario aperta a tecla 0, o
   * componente detecta que o valor nao mudou (isValueChanged retorna false) e
   * descarta o keystroke. Como agora o campo SEMPRE comeca com 0 na criacao,
   * isso afetaria todos os tipos de item.
   *
   * Solucao: ao focar com valor 0, limpa para null. Assim o usuario digita 0
   * normalmente (null -> 0 dispara mudanca) e, se sair sem digitar nada, o
   * handler onPrecoDeVendaBlur() restaura o 0.
   */
  onPrecoDeVendaFocus(): void {
    if (this.form.precoDeVenda === 0) {
      this.form.precoDeVenda = null as any;
    }
  }

  /**
   * Handler chamado ao sair do campo de preco de venda.
   * Se o campo ficou vazio (usuario nao digitou nada), restaura para 0 —
   * preco zero e valido para qualquer tipo de item.
   */
  onPrecoDeVendaBlur(): void {
    if (this.form.precoDeVenda == null) {
      this.form.precoDeVenda = 0;
    }
  }

  /** Atualiza casas decimais de estoque com base na unidade selecionada e reformata valores */
  private atualizarCasasDecimaisEstoque(unidadeId: number): void {
    const novasCasas = this.unidadeMedidaCasasDecimaisMap.get(unidadeId) ?? 0;
    const casasAnteriores = this.casasDecimaisEstoque;
    this.casasDecimaisEstoque = novasCasas;

    // Reformatar valores existentes se casas decimais diminuiu
    if (novasCasas < casasAnteriores) {
      if (this.form.saldo != null) {
        this.form.saldo = this.arredondarParaCasas(this.form.saldo, novasCasas);
      }
      if (this.form.estoqueMinimo != null) {
        this.form.estoqueMinimo = this.arredondarParaCasas(this.form.estoqueMinimo, novasCasas);
      }
    }
  }

  /** Arredonda um valor para o numero de casas decimais especificado */
  private arredondarParaCasas(valor: number, casas: number): number {
    const fator = Math.pow(10, casas);
    return Math.round(valor * fator) / fator;
  }

  loadGrupos(): void {
    this.grupoService.get('/grupo').subscribe({
      next: (response) => {
        this.grupos = response.content.map((grupo: { nome: string; id?: number }) => ({
          label: grupo.nome, // Garantido como string
          value: grupo.id ?? 0, // Garantido como number
        }));
      },
      error: () => {
        // Grupos são opcionais — falha silenciosa
        this.grupos = [];
      },
    });
  }

  loadSubGrupos(): void {
    this.subGrupoService.get('/subgrupo').subscribe({
      next: (response) => {
        this.subGrupos = response.content.map((subGrupo: { nome: string; id?: number }) => ({
          label: subGrupo.nome, // Garantido como string
          value: subGrupo.id ?? 0, // Garantido como number
        }));
      },
      error: () => {
        // Subgrupos são opcionais — falha silenciosa
        this.subGrupos = [];
      },
    });
  }

  loadMarcas(): void {
    this.marcaService.get('/marca').subscribe({
      next: (response) => {
        this.marcas = response.content.map((marca: { nome: string; id?: number }) => ({
          label: marca.nome, // Garantido como string
          value: marca.id ?? 0, // Garantido como number
        }));
      },
      error: () => {
        // Marcas são opcionais — falha silenciosa
        this.marcas = [];
      },
    });
  }

  loadFornecedores(): void {
    this.pessoaService.get('/pessoas/buscar?categoriaFornecedor=true').subscribe({
      next: (response) => {
        this.fornecedores = (response?.content || []).map((fornecedor) => ({
          label: fornecedor.nome,
          value: fornecedor.id ?? 0,
        }));
      },
      error: () => {
        // Ausência de fornecedores é estado válido — não exibir toast de erro
        this.fornecedores = [];
      },
    });
  }

  loadEmpresaConfig(): void {
    this.empresaConfigService.getById('/configuracaoEmpresa/sessao').subscribe({
      next: (empresa: any) => {
        this.habilitarMiniShoppingEmpresaConfig = empresa.habilitarMiniShopping || false;
        this.imprimirItensDeAcordoImpressoraEmpresaConfig = empresa.imprimirItensDeAcordoImpressora || false;
      },
      error: (err) => console.error('Erro ao carregar configuração da empresa:', err),
    });
  }

  loadImpressoras(): void {
    this.impressoraService.get('/impressora').subscribe({
      next: (response) => {
        this.impressoras = (Array.isArray(response) ? response : []).map((impressora: { nome: string; codigoImpressora?: number }) => ({
          label: impressora.nome,
          value: impressora.codigoImpressora ?? 0,
        }));
      },
      error: () => {
        // Impressoras são opcionais — falha silenciosa
        this.impressoras = [];
      },
    });
  }

  private buildTipoDeItensOptions() {
    this.tipoDeItensOptions = Object.values(TipoItem).map(filtro => ({
      value: filtro,
      label: this.translate.instant(`tipo_de_item.${filtro}`)
    }));
  }

  /** Pre-seleciona "Mercadoria" como tipo de item padrao */
  private preSelectTipoItem(): void {
    const mercadoria = this.tipoDeItensOptions.find(o => o.value === TipoItem.MERCADORIA);
    if (mercadoria && !this.form.tipoItem?.value) {
      this.form.tipoItem = mercadoria as any;
    }
  }

  private buildTipoDeImpostoOptions() {
    this.tipoDeImpostoOptions = Object.values(TipoDeImposto).map(filtro => ({
      value: filtro,
      label: this.translate.instant(`tipo_de_imposto.${filtro}`)
    }));
  }

  private mapFormToApiPayload(form: ProdutosDTO): any {
    const { saldo, ...formSemSaldo } = form as any;
    const miniShopAtivo = this.toBoolean(form.habilitarMiniShopping);
    return {
      ...formSemSaldo,
      grupo_id: this.getValueOrNull(form.grupo_id),
      subGrupo_id: this.getValueOrNull(form.subGrupo_id),
      marca_id: this.getValueOrNull(form.marca_id),
      fornecedorPrincipal_id: this.getValueOrNull(form.fornecedorPrincipal_id),
      unidadeMedidaDeItem_id: this.getValueOrNull(form.unidadeMedidaDeItem_id),

      tipoDeImposto: this.getStringValue(form.tipoDeImposto),
      tipoDeDesconto: this.getStringValue(form.tipoDeDesconto),
      tipoItem: this.getStringValue(form.tipoItem),

      status: this.toBoolean(form.status),
      venderNoMiniShop: this.toBoolean(form.habilitarMiniShopping),
      permiteImprimir: this.toBoolean(form.permiteImprimir),

      precoDeVenda: this.toNumber(form.precoDeVenda),
      custo: this.toNumber(form.custo),
      desconto: this.toNumber(form.desconto),
      estoqueMinimo: this.toNumber(form.estoqueMinimo),
      impressoraPadraoId: this.getValueOrNull(form.impressoraPadraoId),

      // Promocao/Novidade: so e enviada quando o MiniShop esta ativo (secao visivel).
      // Com MiniShop desligado, zera tudo para nao gravar promocao/novidade "fantasma".
      // Data serializada como ISO local apenas quando a flag esta ligada (RN05).
      promocao: miniShopAtivo && this.promocao,
      promocaoAteEm: miniShopAtivo && this.promocao && this.promocaoAteEmData ? toLocalISOString(this.promocaoAteEmData) : null,
      novidade: miniShopAtivo && this.novidade,
      novidadeAteEm: miniShopAtivo && this.novidade && this.novidadeAteEmData ? toLocalISOString(this.novidadeAteEmData) : null,
    };
  }

  private getValueOrNull(obj: any): number | null {
    return obj && typeof obj === 'object' && 'value' in obj ? obj.value : obj ?? null;
  }

  private getStringValue(obj: any): string | null {
    if (!obj || (typeof obj === 'string' && obj.trim() === '')) {
      return null;
    }
    return obj && typeof obj === 'object' && 'value' in obj ? obj.value : obj;
  }

  private toNumber(value: any): number {
    return value !== undefined && value !== null ? Number(value) : 0;
  }

  private toBoolean(value: any): boolean {
    return value === true || value === 'true';
  }

  isFormSubmitted = false;
  submitting = false;

  // ==========================
  // VARIANTES DE PRECO (locais)
  // ==========================

  adicionarVariante(): void {
    this.variantes.push({
      item_id: 0, // sera preenchido apos criar o produto
      descricao: '',
      precoDeVenda: 0,
    });
  }

  removerVariante(index: number): void {
    this.variantes.splice(index, 1);
  }

  // ==========================
  // GRUPOS DE MODIFICADORES (locais)
  // ==========================

  adicionarGrupoModificador(): void {
    this.gruposModificadores.push({
      item_id: 0, // sera preenchido apos criar o produto
      descricao: '',
      maximoDeModificadores: 1,
      minimoDeModificadores: 0,
      modificadores: [],
    });
  }

  removerGrupoModificador(index: number): void {
    this.gruposModificadores.splice(index, 1);
  }

  adicionarModificador(grupoIndex: number): void {
    const grupo = this.gruposModificadores[grupoIndex];
    grupo.modificadores.push({
      item_id: 0, // sera preenchido apos criar o produto
      grupoDeModificadorDePrecoDeItem_id: 0, // sera preenchido apos salvar o grupo
      descricao: '',
      precoDeVenda: 0,
    });
  }

  removerModificador(grupoIndex: number, modIndex: number): void {
    this.gruposModificadores[grupoIndex].modificadores.splice(modIndex, 1);
  }

  /** Toggle: "Cliente deve escolher?" → mapeia para minimoDeModificadores */
  onToggleObrigatorio(grupoIndex: number, obrigatorio: boolean): void {
    this.gruposModificadores[grupoIndex].minimoDeModificadores = obrigatorio ? 1 : 0;
  }

  /** Toggle: "Pode escolher mais de um?" → mapeia para maximoDeModificadores */
  onToggleMultiplo(grupoIndex: number, multiplo: boolean): void {
    this.gruposModificadores[grupoIndex].maximoDeModificadores = multiplo ? 3 : 1;
  }

  // ==========================
  // SUBMIT (cria produto, depois variantes e modificadores)
  // ==========================

  submit(): void {
    if (this.submitting) return;
    this.isFormSubmitted = true;
    this.form.status = true; // forca ativo por padrao

    const unidadeValida = this.form.unidadeMedidaDeItem_id != null
      && this.form.unidadeMedidaDeItem_id !== 0;

    const ehServico = this.ehServico;

    // Preco de venda e valido com 0 ou positivo para qualquer tipo de item.
    // Invalido apenas se nao foi preenchido (null/undefined).
    const precoInvalido = this.precoDeVendaInvalido;

    const camposFaltantes: string[] = [];
    if (!this.form.nome) camposFaltantes.push(this.translate.instant('produtos.labels.nome'));
    if (!this.form.grupo_id) camposFaltantes.push(this.translate.instant('produtos.labels.grupo'));
    if (!this.form.tipoItem) camposFaltantes.push(this.translate.instant('produtos.labels.tipo_de_item'));
    if (!this.form.tipoDeImposto) camposFaltantes.push(this.translate.instant('produtos.labels.tipo_de_imposto'));
    if (!unidadeValida) camposFaltantes.push(this.translate.instant('produtos.labels.unidade_de_medida'));
    if (precoInvalido) camposFaltantes.push(this.translate.instant('produtos.labels.preco_de_venda'));
    if (!ehServico && this.form.custo == null) camposFaltantes.push(this.translate.instant('produtos.labels.custo'));
    if (!ehServico && this.form.saldo == null) camposFaltantes.push(this.translate.instant('produtos.labels.saldo'));
    if (!ehServico && this.form.estoqueMinimo == null) camposFaltantes.push(this.translate.instant('produtos.labels.estoque_minimo'));

    if (camposFaltantes.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('produtos.mensagens.campos_obrigatorios_titulo'),
        detail: this.translate.instant('produtos.mensagens.campos_obrigatorios_prefixo') + camposFaltantes.join(', '),
        life: 6000,
      });
      return;
    }

    // Validacao client-side de promocao/novidade (RN01-RN04).
    // So exigida quando a secao esta visivel (MiniShop ativo).
    if (this.form.habilitarMiniShopping && !this.validarPromocaoNovidadeOuAvisar()) {
      return;
    }

    const saldoInicial = this.toNumber(this.form.saldo);
    const payload = this.mapFormToApiPayload(this.form);

    this.submitting = true;
    this.produtosService.post('/item', payload).subscribe({
      next: (produtoCriado: any) => {
        const produtoId = produtoCriado?.id;

        // Criar ajuste de estoque ENTRADA se saldo > 0
        if (produtoId && saldoInicial > 0) {
          const dto: AjusteDeEstoqueDTO = {
            tipoOperacaoDeEstoque: TipoOperacaoDeEstoque.ENTRADA,
            item_id: produtoId,
            quantidade: saldoInicial,
            emissao: new Date().toISOString().replace('Z', ''),
            tipoContaEstoque: TipoContaEstoque.NORMAL,
          };
          this.ajusteEstoqueService.post('/ajusteDeEstoque', dto).subscribe({
            next: () => {
              this.continuarAposCriacao(produtoId);
            },
            error: () => {
              this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('common.sucesso'),
                detail: this.translate.instant('produtos.mensagens.ajuste_estoque_erro'),
              });
              // Continua mesmo com erro no ajuste - produto ja foi salvo
              this.continuarAposCriacao(produtoId);
            },
          });
        } else {
          this.continuarAposCriacao(produtoId);
        }
      },
      error: (err) => {
        this.submitting = false;
        this.tratarErroSalvar(err);
      },
    });
  }

  /**
   * Valida promocao/novidade e, se houver erros, exibe-os via MessageService.
   * @returns `true` quando esta valido (pode prosseguir com o submit).
   */
  private validarPromocaoNovidadeOuAvisar(): boolean {
    const validacao = validarPromocaoNovidade(
      this.promocao,
      this.promocaoAteEmData,
      this.novidade,
      this.novidadeAteEmData,
    );
    if (!validacao.ok) {
      validacao.errosI18n.forEach((chave) => {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('produtos.mensagens.campos_obrigatorios_titulo'),
          detail: this.translate.instant(chave),
          life: 6000,
        });
      });
      return false;
    }
    return true;
  }

  /** Exibe erro de salvamento — detalha array `erros` 400 (RN01-RN04) ou cai no generico. */
  private tratarErroSalvar(err: any): void {
    const mensagemPadrao = this.translate.instant('produtos.texts.erro_cadastrar_produto');
    const erros400 = extrairErros400(err, mensagemPadrao);
    if (err?.status === 400 && erros400.length > 0) {
      erros400.forEach((detail) => this.showError(detail));
      return;
    }
    this.showError(sanitizarMensagemErro(err?.error?.message, mensagemPadrao));
  }

  private continuarAposCriacao(produtoId: number): void {
    if (produtoId && (this.variantes.length > 0 || this.gruposModificadores.length > 0)) {
      this.salvarVariantesEModificadores(produtoId);
    } else {
      this.finalizarCriacao();
    }
  }

  private salvarVariantesEModificadores(produtoId: number): void {
    const requests: any[] = [];

    // Salvar variantes
    for (const variante of this.variantes) {
      if (variante.descricao?.trim()) {
        requests.push(
          this.produtoVarianteModificadorService.createVariante({
            ...variante,
            item_id: produtoId,
          })
        );
      }
    }

    // Salvar grupos de modificadores (sem opcoes primeiro)
    // As opcoes serao salvas apos o grupo ser criado
    const gruposParaSalvar = this.gruposModificadores.filter(g => g.descricao?.trim());

    if (requests.length === 0 && gruposParaSalvar.length === 0) {
      this.finalizarCriacao();
      return;
    }

    // Salvar variantes primeiro
    if (requests.length > 0) {
      forkJoin(requests).subscribe({
        next: () => {
          if (gruposParaSalvar.length > 0) {
            this.salvarGruposModificadoresSequencial(produtoId, gruposParaSalvar, 0);
          } else {
            this.finalizarCriacao();
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('produtos.mensagens.produto_cadastrado_variantes_erro'),
          });
          this.finalizarCriacao();
        },
      });
    } else if (gruposParaSalvar.length > 0) {
      this.salvarGruposModificadoresSequencial(produtoId, gruposParaSalvar, 0);
    }
  }

  private salvarGruposModificadoresSequencial(
    produtoId: number,
    grupos: GrupoDeModificadorDePrecoDeItemDTO[],
    index: number
  ): void {
    if (index >= grupos.length) {
      this.finalizarCriacao();
      return;
    }

    const grupo = grupos[index];
    const modificadoresLocais = grupo.modificadores.filter(m => m.descricao?.trim());

    this.produtoVarianteModificadorService.createGrupo({
      ...grupo,
      item_id: produtoId,
      modificadores: [],
    }).subscribe({
      next: (grupoCriado) => {
        // Salvar as opcoes do grupo recem-criado
        if (grupoCriado.id && modificadoresLocais.length > 0) {
          const modRequests = modificadoresLocais.map(mod =>
            this.produtoVarianteModificadorService.createModificador({
              ...mod,
              item_id: produtoId,
              grupoDeModificadorDePrecoDeItem_id: grupoCriado.id!,
            })
          );

          forkJoin(modRequests).subscribe({
            next: () => this.salvarGruposModificadoresSequencial(produtoId, grupos, index + 1),
            error: () => this.salvarGruposModificadoresSequencial(produtoId, grupos, index + 1),
          });
        } else {
          this.salvarGruposModificadoresSequencial(produtoId, grupos, index + 1);
        }
      },
      error: () => {
        // Continua com o proximo grupo mesmo se um falhar
        this.salvarGruposModificadoresSequencial(produtoId, grupos, index + 1);
      },
    });
  }

  private finalizarCriacao(): void {
    this.submitting = false;
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('common.sucesso'),
      detail: this.translate.instant('produtos.mensagens.produto_cadastrado'),
    });
    this.form = this.createEmptyForm();
    this.variantes = [];
    this.gruposModificadores = [];
    this.isFormSubmitted = false;
    this.casasDecimaisEstoque = 0;
    this.navigateListaDeProdutos();
  }

  // ========= FILE UPLOAD =========
  private readonly formatosImagemPermitidos = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

  async onSelectImage(event: any) {
    const file: File | undefined = event.files?.[0];
    if (!file) { return; }

    if (!this.formatosImagemPermitidos.includes(file.type)) {
      this.messageService.add({ severity: 'error', summary: this.translate.instant('common.arquivo_invalido'), detail: this.translate.instant('produtos.mensagens.formato_imagem_invalido') });
      return;
    }

    this.fileName = file.name;

    // DataURL (para preview) e Base64 sem prefixo (para enviar)
    const imagem = await ImagemUtils.comprimirParaBase64(file);
    this.imagePreviewUrl = imagem.dataUrl;
    this.imageBase64 = imagem.base64;

    this.form.imagem = this.imageBase64;
  }

  onClearImage() {
    this.fileName = null;
    this.imageBase64 = null;
    this.imagePreviewUrl = null;
    this.form.imagem = '';
  }

  // =================================

  private createEmptyForm(): ProdutosDTO {
    return {
      nome: '',
      barras: '',
      codigoFabricante: '',
      unidadeMedidaDeItem_id: undefined,
      precoDeVenda: 0,
      tipoDeImposto: 'IVA_10',
      custo: null as any,
      grupo_id: undefined,
      subGrupo_id: undefined,
      marca_id: undefined,
      estoqueMinimo: null as any,
      tipoDeDesconto: 'SEM_DESCONTO',
      desconto: 0,
      fornecedorPrincipal_id: undefined,
      status: true,
      saldo: null as any,
      imagem: undefined,
      descricao: '',
      tipoItem: undefined as any,
      habilitarMiniShopping: false,
      impressoraPadraoId: undefined,
      permiteImprimir: false,
    };
  }

  private showError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: this.translate.instant('common.erro'), detail });
  }

  // Atalhos de teclado
  @HostListener('document:keydown', ['$event'])
  onKeydown(ev: KeyboardEvent): void {
    // Ctrl+Enter: salvar produto
    if (ev.ctrlKey && ev.key === 'Enter') {
      ev.preventDefault();
      this.submit();
      return;
    }

    // Escape: voltar para lista
    if (ev.key === 'Escape') {
      ev.preventDefault();
      this.navigateListaDeProdutos();
      return;
    }
  }

  /** Quando o grupo muda, limpa a seleção de subgrupo */
  onGrupoChange(grupoId: any): void {
    this.form.subGrupo_id = undefined;
  }

  /**
   * Ao desligar o MiniShop, a secao de promocao/novidade fica oculta;
   * zera as flags e datas para nao gravar promocao/novidade "fantasma".
   */
  onMiniShopChange(): void {
    if (!this.form.habilitarMiniShopping) {
      this.promocao = false;
      this.promocaoAteEmData = null;
      this.novidade = false;
      this.novidadeAteEmData = null;
    }
  }

  /** Ao desligar o switch de promocao, zera a data (evita "valor fantasma" — RN05). */
  onPromocaoChange(): void {
    if (!this.promocao) {
      this.promocaoAteEmData = null;
    }
  }

  /** Ao desligar o switch de novidade, zera a data (evita "valor fantasma" — RN05). */
  onNovidadeChange(): void {
    if (!this.novidade) {
      this.novidadeAteEmData = null;
    }
  }

  navigateListaDeProdutos() {
    this.router.navigate(['lista-de-produtos']);
  }
}
